import { NativeSelect, type NativeSelectProps } from "@/components/ui/native-select";
import type { Prompt, PromptType } from "../../../types";

type PromptOptionGroup = {
  key: string;
  label: string;
  prompts: Prompt[];
};

interface PromptSelectProps extends Omit<NativeSelectProps, "children"> {
  prompts: Prompt[];
  placeholder?: string;
}

const promptTypeOrder: PromptType[] = ["scoring", "sentiment"];

/**
 * Gets the prompt type label used in grouped selectors.
 *
 * @param type Prompt type.
 * @return Human-readable prompt type label.
 */
function getPromptTypeLabel(type: PromptType): string {
  return type === "sentiment" ? "Sentiment queries" : "Scoring prompts";
}

/**
 * Normalizes a prompt category into the visible Topic value.
 *
 * @param category Stored prompt category.
 * @return Normalized topic key.
 */
function getPromptTopic(category: string | undefined): string {
  const topic = category?.trim();

  return topic || "general";
}

/**
 * Formats a stored prompt topic for select group labels.
 *
 * @param topic Normalized topic key.
 * @return Human-readable topic label.
 */
function formatTopicLabel(topic: string): string {
  return topic.toLowerCase() === "general" ? "General" : topic;
}

/**
 * Groups prompts by prompt type and topic while preserving prompt order within each group.
 *
 * @param prompts Active prompts to group.
 * @return Prompt option groups for rendering.
 */
function groupPromptsByTypeAndTopic(prompts: Prompt[]): PromptOptionGroup[] {
  const groups = new Map<string, PromptOptionGroup>();

  promptTypeOrder.forEach((type) => {
    prompts
      .filter((prompt) => prompt.type === type)
      .forEach((prompt) => {
        const topic = getPromptTopic(prompt.category);
        const key = `${type}:${topic.toLowerCase()}`;

        if (!groups.has(key)) {
          groups.set(key, {
            key,
            label: `${getPromptTypeLabel(type)} — ${formatTopicLabel(topic)}`,
            prompts: [],
          });
        }

        groups.get(key)!.prompts.push(prompt);
      });
  });

  return Array.from(groups.values());
}

/**
 * Renders a prompt select grouped by Topic while preserving scoring/sentiment labels.
 *
 * @param props Prompt select props.
 * @return Prompt select control.
 */
export function PromptSelect({ prompts, placeholder = "Select a prompt...", ...props }: PromptSelectProps) {
  const groups = groupPromptsByTypeAndTopic(prompts);

  return (
    <NativeSelect {...props}>
      <option value="">{placeholder}</option>
      {groups.map((group) => (
        <optgroup key={group.key} label={group.label}>
          {group.prompts.map((prompt) => (
            <option key={prompt.id} value={prompt.id}>
              {prompt.name}
            </option>
          ))}
        </optgroup>
      ))}
    </NativeSelect>
  );
}
